import { MoneyDisplay } from "@/components/accounting/money-display";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import type { MatchProposal } from "@/gen/omnios/v1/accounting_pb";
import {
	useAcceptMatch,
	useFinalizeRecon,
	useGetAccountStatement,
	useGetReconSession,
	useRunAutoMatch,
} from "@/hooks/use-accounting";
import { useAccountingShortcuts } from "@/hooks/use-accounting-shortcuts";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { formatDate } from "@/lib/format";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";

import type { ColDef, ValueFormatterParams } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { createFileRoute } from "@tanstack/react-router";
import { Check, CheckCircle2, Loader2, Search, Sparkles, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/accounting/bank/$id")({
	component: ReconciliationPage,
});

function formatAmount(value: number): string {
	return new Intl.NumberFormat("ru-RU", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value);
}

function ReconciliationPage() {
	const { t } = useTranslation("accounting");
	const { id } = Route.useParams();
	const gridRef = useRef<AgGridReact>(null);
	const { data: session, isLoading } = useGetReconSession(id);
	const runAutoMatch = useRunAutoMatch();
	const finalizeRecon = useFinalizeRecon();
	const acceptMatch = useAcceptMatch();

	const [proposals, setProposals] = useState<MatchProposal[]>([]);
	const [selectedProposal, setSelectedProposal] = useState<number | null>(null);

	useBreadcrumbs([
		{ label: t("bank_accounts"), href: "/accounting/bank" },
		{ label: session?.bankAccountId ? session.bankAccountId.slice(0, 8) : id },
	]);

	// Fetch book entries for this bank account
	const { data: bookStatement } = useGetAccountStatement(
		session?.bankAccountId ? "51" : "",
	);

	const bookEntries = bookStatement?.lines ?? [];
	type BookEntry = (typeof bookEntries)[number];

	const bookColumnDefs = useMemo<ColDef<BookEntry>[]>(
		() => [
			{
				field: "date",
				headerName: t("entry_date"),
				width: 90,
				valueFormatter: (p: ValueFormatterParams<BookEntry>) => {
					const d = p.data?.date;
					return d ? formatDate(d.toDate(), "dd.MM") : "—";
				},
			},
			{
				field: "entryNumber",
				headerName: "№",
				width: 80,
				cellClass: "font-mono text-xs",
			},
			{
				field: "description",
				headerName: t("entry_description"),
				flex: 1,
				minWidth: 120,
			},
			{
				headerName: t("line_debit"),
				width: 110,
				type: "rightAligned",
				cellClass: "font-tabular",
				valueGetter: (p) => Number.parseFloat(p.data?.debit || "0") || null,
				valueFormatter: (p: ValueFormatterParams) =>
					p.value ? formatAmount(p.value) : "",
			},
			{
				headerName: t("line_credit"),
				width: 110,
				type: "rightAligned",
				cellClass: "font-tabular",
				valueGetter: (p) => Number.parseFloat(p.data?.credit || "0") || null,
				valueFormatter: (p: ValueFormatterParams) =>
					p.value ? formatAmount(p.value) : "",
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			filter: true,
			filterParams: {
				buttons: ["apply", "reset"],
				closeOnApply: true,
			},
		}),
		[],
	);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "matched",
				content: `${t("recon_matched")}: ${session?.matchCount ?? 0} / ${(session?.matchCount ?? 0) + (session?.unmatchedCount ?? 0)}`,
				align: "left" as const,
			},
			{
				id: "diff",
				content: `${t("recon_difference")}: ${(Number.parseFloat(session?.statementBalance || "0") - Number.parseFloat(session?.bookBalance || "0")).toFixed(2)}`,
				align: "left" as const,
			},
		],
		[session, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	// Keyboard shortcuts
	useAccountingShortcuts(
		useMemo(
			() => ({
				A: () => {
					if (selectedProposal != null && proposals[selectedProposal]) {
						acceptMatch.mutate({
							sessionId: id,
							statementLineId: proposals[selectedProposal].statementLineId,
							bookEntryId: proposals[selectedProposal].bookEntryId,
						});
						setProposals((prev) =>
							prev.filter((_, i) => i !== selectedProposal),
						);
						setSelectedProposal(null);
					}
				},
				R: () => {
					if (selectedProposal != null) {
						setProposals((prev) =>
							prev.filter((_, i) => i !== selectedProposal),
						);
						setSelectedProposal(null);
					}
				},
				Space: () => {
					if (selectedProposal == null) {
						setSelectedProposal(0);
					} else if (selectedProposal < proposals.length - 1) {
						setSelectedProposal(selectedProposal + 1);
					}
				},
			}),
			[selectedProposal, proposals, acceptMatch, id],
		),
	);

	if (isLoading) {
		return (
			<div className="flex flex-col h-full">
				<div className="grid grid-cols-4 gap-px bg-border border-b shrink-0">
					{Array.from({ length: 4 }).map((_, i) => (
						<div key={i} className="bg-background px-3 py-2">
							<div className="h-3 w-20 bg-muted/50 rounded animate-pulse mb-1" />
							<div className="h-5 w-28 bg-muted/50 rounded animate-pulse" />
						</div>
					))}
				</div>
				<div className="flex-1 p-3 space-y-2">
					{Array.from({ length: 8 }).map((_, i) => (
						<div key={i} className="h-7 bg-muted/50 rounded animate-pulse" />
					))}
				</div>
			</div>
		);
	}

	const diff =
		Number.parseFloat(session?.statementBalance || "0") -
		Number.parseFloat(session?.bookBalance || "0");

	return (
		<div className="flex flex-col h-full">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption(
								"quickFilterText",
								e.target.value,
							)
						}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => {
						for (const p of proposals.filter((p) => p.confidence >= 0.9)) {
							acceptMatch.mutate({
								sessionId: id,
								statementLineId: p.statementLineId,
								bookEntryId: p.bookEntryId,
							});
						}
					}}
					disabled={proposals.length === 0}
				>
					{t("recon_accept_all", { defaultValue: "Принять все >90%" })}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => finalizeRecon.mutate({ sessionId: id })}
					disabled={finalizeRecon.isPending || session?.status === "finalized"}
				>
					{t("recon_finalize")}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({
							fileName: `recon_${id}.csv`,
						})
					}
				>
					CSV
				</Button>
				<Button
					size="sm"
					className="h-9"
					onClick={() => {
						runAutoMatch.mutate(
							{ sessionId: id },
							{ onSuccess: (data) => setProposals(data.proposals) },
						);
					}}
					disabled={runAutoMatch.isPending}
				>
					{t("recon_auto_match")}
				</Button>
			</div>
			{/* Summary bar */}
			<div className="grid grid-cols-4 gap-px bg-border border-b shrink-0">
				<div className="bg-background px-3 py-2">
					<p className="text-[10px] text-muted-foreground">
						{t("recon_statement_balance")}
					</p>
					<MoneyDisplay
						amount={session?.statementBalance ?? "0"}
						className="text-sm font-semibold tabular-nums"
					/>
				</div>
				<div className="bg-background px-3 py-2">
					<p className="text-[10px] text-muted-foreground">
						{t("recon_book_balance")}
					</p>
					<MoneyDisplay
						amount={session?.bookBalance ?? "0"}
						className="text-sm font-semibold tabular-nums"
					/>
				</div>
				<div className="bg-background px-3 py-2">
					<p className="text-[10px] text-muted-foreground">
						{t("recon_difference")}
					</p>
					<MoneyDisplay
						amount={String(diff)}
						className={`text-sm font-semibold tabular-nums ${Math.abs(diff) < 0.01 ? "text-positive" : "text-negative"}`}
					/>
				</div>
				<div className="bg-background px-3 py-2">
					<p className="text-[10px] text-muted-foreground">
						{t("recon_matched")}
					</p>
					<p className="text-sm font-semibold tabular-nums">
						{session?.matchCount ?? 0}
						<span className="text-muted-foreground font-normal">
							{" "}
							/ {(session?.matchCount ?? 0) + (session?.unmatchedCount ?? 0)}
						</span>
					</p>
				</div>
			</div>

			{/* Three-panel layout */}
			<div className="flex-1 grid grid-cols-[1fr_1fr_1fr] min-h-0">
				{/* Left: Book entries — AG Grid */}
				<div className="flex flex-col border-r">
					<div className="px-3 py-1.5 border-b text-xs font-semibold flex items-center justify-between">
						<span>{t("recon_book_entries")}</span>
						<span className="text-muted-foreground font-normal">
							{bookEntries.length}
						</span>
					</div>
					<div className="flex-1 ag-theme-quartz" style={{ width: "100%" }}>
						<AgGridReact<BookEntry>
							ref={gridRef}
							rowData={bookEntries}
							columnDefs={bookColumnDefs}
							defaultColDef={defaultColDef}
							localeText={AG_GRID_LOCALE_RU}
							rowHeight={28}
							headerHeight={30}
							animateRows={false}
							getRowId={(p) =>
								`${p.data.entryId}-${p.data.debit}-${p.data.credit}`
							}
							overlayNoRowsTemplate={`<div style="text-align:center;padding:24px"><p style="font-size:12px;color:hsl(var(--muted-foreground))">${t("recon_no_book_entries")}</p></div>`}
						/>
					</div>
				</div>

				{/* Center: Match proposals */}
				<div className="flex flex-col border-r">
					<div className="px-3 py-1.5 border-b text-xs font-semibold flex items-center justify-between">
						<span>{t("recon_matched_pairs")}</span>
						<span className="text-muted-foreground font-normal">
							{proposals.length}
						</span>
					</div>
					<div className="flex-1 overflow-y-auto">
						{runAutoMatch.isPending ? (
							<div className="flex items-center justify-center py-12">
								<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
							</div>
						) : proposals.length === 0 ? (
							<div className="flex flex-col items-center justify-center py-12 px-4">
								<Sparkles className="h-5 w-5 text-muted-foreground mb-2" />
								<p className="text-xs text-muted-foreground text-center">
									{t("recon_no_matches")}
								</p>
							</div>
						) : (
							<div className="divide-y">
								{proposals.map((p, i) => {
									const isSelected = selectedProposal === i;
									const confColor =
										p.confidence >= 0.9
											? "text-positive"
											: p.confidence >= 0.7
												? "text-amber-500"
												: "text-negative";
									return (
										<div
											key={`${p.statementLineId}-${p.bookEntryId}`}
											className={`px-3 py-2 cursor-pointer transition-colors ${isSelected ? "bg-accent" : "hover:bg-accent/30"}`}
											onClick={() => setSelectedProposal(i)}
											onKeyDown={(e) => {
												if (e.key === "Enter") setSelectedProposal(i);
											}}
											role="button"
											tabIndex={0}
										>
											<div className="flex items-center justify-between mb-1">
												<span
													className={`text-xs font-semibold tabular-nums ${confColor}`}
												>
													{Math.round(p.confidence * 100)}%
												</span>
												<Badge
													variant="outline"
													className="text-[9px] px-1 py-0"
												>
													{t(`recon_strategy_${p.strategy.replace("_", "")}`, {
														defaultValue: p.strategy,
													})}
												</Badge>
											</div>
											<div className="flex items-center justify-between text-[10px] text-muted-foreground">
												<span className="font-mono">
													{p.statementLineId.slice(0, 8)}
												</span>
												<span>↔</span>
												<span className="font-mono">
													{p.bookEntryId.slice(0, 8)}
												</span>
											</div>
											{p.reason && (
												<p className="text-[10px] text-muted-foreground mt-0.5 truncate">
													{p.reason}
												</p>
											)}
											{/* Actions */}
											<div className="flex items-center gap-1 mt-1.5">
												<Button
													variant="ghost"
													size="sm"
													className="gap-1 px-2 py-0.5 h-auto text-[10px] bg-green-500/10 text-green-600 hover:bg-green-500/20"
													onClick={(e) => {
														e.stopPropagation();
														acceptMatch.mutate({
															sessionId: id,
															statementLineId: p.statementLineId,
															bookEntryId: p.bookEntryId,
														});
														setProposals((prev) =>
															prev.filter((_, idx) => idx !== i),
														);
													}}
												>
													<Check className="h-3 w-3" /> {t("recon_accept")}
												</Button>
												<Button
													variant="ghost"
													size="sm"
													className="gap-1 px-2 py-0.5 h-auto text-[10px] bg-red-500/10 text-red-500 hover:bg-red-500/20"
													onClick={(e) => {
														e.stopPropagation();
														setProposals((prev) =>
															prev.filter((_, idx) => idx !== i),
														);
													}}
												>
													<X className="h-3 w-3" /> {t("recon_reject")}
												</Button>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</div>
				</div>

				{/* Right: Statement summary */}
				<div className="flex flex-col">
					<div className="px-3 py-1.5 border-b text-xs font-semibold">
						{t("recon_bank_lines")}
					</div>
					<div className="flex-1 overflow-y-auto">
						<div className="px-3 py-3 space-y-3">
							<div className="flex items-center justify-between">
								<span className="text-xs text-muted-foreground">
									{t("recon_statement_balance")}
								</span>
								<MoneyDisplay
									amount={session?.statementBalance ?? "0"}
									className="text-sm font-medium"
								/>
							</div>
							<div className="flex items-center justify-between">
								<span className="text-xs text-muted-foreground">
									{t("recon_statement_date")}
								</span>
								<span className="text-sm">
									{session?.statementDate
										? formatDate(session.statementDate.toDate())
										: "—"}
								</span>
							</div>
							<div className="flex items-center justify-between">
								<span className="text-xs text-muted-foreground">
									{t("recon_unmatched")}
								</span>
								<span className="text-sm font-medium tabular-nums">
									{session?.unmatchedCount ?? 0}
								</span>
							</div>
							<div className="flex items-center justify-between">
								<span className="text-xs text-muted-foreground">
									{t("entry_status")}
								</span>
								<Badge
									variant={
										session?.status === "finalized" ? "default" : "outline"
									}
								>
									{t(`recon_status_${session?.status || "in_progress"}`)}
								</Badge>
							</div>
						</div>
						{session?.status === "finalized" && (
							<div className="px-3 py-3 border-t">
								<div className="flex items-center gap-2 text-positive">
									<CheckCircle2 className="h-4 w-4" />
									<span className="text-xs font-medium">
										{t("recon_finalized_message", {
											defaultValue: "Сверка завершена",
										})}
									</span>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
